// Hero i profil psa: wejście (litery, etykieta, zdjęcie, naklejka), paralaksa za myszką i naklejka do rzucania.
import {gsap, SplitText, $$, has, wait} from "./shared";
import {tickText} from "./counters";
import {pawBurstFrom} from "./confetti";

export async function initHeroes({intro, fine}) {
    const heroes = $$(".cc-hero, .cc-profile");
    if (!heroes.length) {
        return;
    }
    if (intro) {
        // Litery tną się dopiero z właściwym fontem (max 1,2 s czekania).
        await Promise.race([document.fonts?.ready ?? Promise.resolve(), wait(1200)]);
    }
    heroes.forEach((hero) => setupHero(hero, {intro, fine}));
}

function setupHero(hero, {intro, fine}) {
    const q = (selector) => hero.querySelector(selector);
    const title = q(".cc-hero__title, .cc-profile__name");
    const eyebrow = q(".cc-eyebrow");
    const leads = hero.querySelectorAll(".cc-hero__lead, .cc-profile__reg, .cc-profile__lead");
    const extras = hero.querySelectorAll(".cc-profile__facts > div, .cc-profile__chips > *");
    const buttons = hero.querySelectorAll(".cc-hero__actions .cc-btn, .cc-profile__actions .cc-btn");
    const sky = q(".cc-hero__sky");
    const photo = q(".cc-hero__photo") || q(".cc-photo--arch");
    const img = photo?.querySelector("img");
    const sticker = q(".cc-sticker");
    const stickerWrap = q(".cc-hero__sticker") || sticker;

    if (intro) {
        const tl = gsap.timeline({defaults: {ease: "back.out(2)"}});
        if (eyebrow) {
            tl.from(eyebrow, {x: -28, autoAlpha: 0, duration: 0.5}, 0);
        }
        if (title) {
            const split = SplitText.create(title, {type: "words,chars", tag: "span", charsClass: "cc-char"});
            tl.from(split.chars, {
                yPercent: 120,
                rotation: () => gsap.utils.random(-30, 30),
                autoAlpha: 0,
                duration: 0.8,
                stagger: 0.028,
                ease: "back.out(2.4)"
            }, 0.1);
            const marks = title.querySelectorAll(".cc-mark");
            if (marks.length) {
                tl.fromTo(marks, {"--mark-size": "0%"}, {"--mark-size": "100%", duration: 0.6, ease: "power3.inOut"}, "-=0.45");
            }
        }
        if (leads.length) {
            tl.from(leads, {y: 24, autoAlpha: 0, duration: 0.7, stagger: 0.08, ease: "power3.out"}, "-=0.35");
        }
        if (extras.length) {
            tl.from(extras, {y: 16, autoAlpha: 0, duration: 0.5, stagger: 0.05}, "-=0.4");
        }
        if (buttons.length) {
            tl.from(buttons, {scale: 0.4, autoAlpha: 0, duration: 0.9, stagger: 0.12, ease: "elastic.out(1, 0.5)"}, "-=0.4");
        }
        if (sky) {
            tl.from(sky, {scale: 0, rotation: -40, duration: 1.2, ease: "elastic.out(1, 0.6)"}, 0.15);
        }
        if (photo) {
            tl.from(photo, {y: 140, rotation: 10, autoAlpha: 0, duration: 1, ease: "back.out(1.4)"}, 0.25);
        }
        if (sticker) {
            tl.from(sticker, {scale: 0, rotation: -220, duration: 1.3, ease: "elastic.out(1, 0.5)"}, 0.8);
            tl.add(() => tickText(sticker.querySelector(".cc-sticker__title"), {duration: 1.2}), 0.9);
        }
    }

    // Spokojny ruch „na żywo”: naklejka się kiwa, niebo oddycha.
    const idle = sticker ? gsap.to(sticker, {rotation: 9, duration: 2.8, ease: "sine.inOut", yoyo: true, repeat: -1, delay: intro ? 2.4 : 0}) : null;
    if (sky) {
        gsap.to(sky, {scale: 1.035, duration: 3.4, ease: "sine.inOut", yoyo: true, repeat: -1, delay: intro ? 1.6 : 0});
    }

    if (!fine) {
        return;
    }

    // Paralaksa: zdjęcie przesuwa się w ramie, niebo i naklejka w przeciwne strony.
    if (img) {
        gsap.set(img, {scale: 1.1});
    }
    const layers = [[img, -14, -10], [sky, 22, 16], [stickerWrap, 30, 24]]
        .filter(([el]) => el)
        .map(([el, ax, ay]) => ({
            x: gsap.quickTo(el, "x", {duration: 0.9, ease: "power3"}),
            y: gsap.quickTo(el, "y", {duration: 0.9, ease: "power3"}),
            ax,
            ay
        }));
    hero.addEventListener("pointermove", (event) => {
        const nx = event.clientX / window.innerWidth - 0.5;
        const ny = event.clientY / window.innerHeight - 0.5;
        layers.forEach((layer) => {
            layer.x(nx * layer.ax);
            layer.y(ny * layer.ay);
        });
    });
    hero.addEventListener("pointerleave", () => layers.forEach((layer) => {
        layer.x(0);
        layer.y(0);
    }));

    if (sticker && has(q(".cc-hero__sticker"))) {
        initStickerDrag(sticker, hero, idle);
    }
}

/** Naklejkę można złapać i rzucić (Draggable + Inertia, dociągane przy pierwszym najechaniu); wraca na miejsce ze sprężynką. */
function initStickerDrag(sticker, bounds, idle) {
    sticker.classList.add("is-draggable");
    sticker.setAttribute("title", "Złap mnie!");

    const home = () => gsap.to(sticker, {
        x: 0,
        y: 0,
        duration: 1.3,
        delay: 0.2,
        ease: "elastic.out(1, 0.35)",
        onComplete: () => idle?.resume()
    });

    const spin = () => {
        idle?.pause();
        gsap.to(sticker, {rotation: "+=360", duration: 0.9, ease: "back.out(1.6)", onComplete: () => idle?.resume()});
        pawBurstFrom(sticker, {count: 70});
    };

    let ready = null;
    const load = () => {
        ready ||= Promise.all([
            import(/* webpackChunkName: "draggable" */ "gsap/Draggable"),
            import(/* webpackChunkName: "draggable" */ "gsap/InertiaPlugin")
        ]).then(([{Draggable}, {InertiaPlugin}]) => {
            gsap.registerPlugin(Draggable, InertiaPlugin);
            Draggable.create(sticker, {
                type: "x,y",
                bounds,
                edgeResistance: 0.7,
                inertia: true,
                zIndexBoost: false,
                onPress() {
                    idle?.pause();
                    gsap.to(sticker, {scale: 1.12, duration: 0.2, ease: "power2.out"});
                },
                onRelease() {
                    gsap.to(sticker, {scale: 1, duration: 0.6, ease: "elastic.out(1, 0.4)"});
                },
                onClick: spin,
                onDragEnd() {
                    if (!this.tween) {
                        home();
                    }
                },
                onThrowComplete: home
            });
        });
        return ready;
    };
    sticker.addEventListener("pointerenter", load, {once: true});
}
